import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { array, object, string } from 'yup';
import { Button, Dialog, DialogContent, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { includes, isEmpty } from 'lodash';

import { useCreateClientInvitationMutation, CreateClientInvitationInput } from 'api';
import { useNotify, useProtectedClient } from 'hooks';

import { Loaders } from '../index';
import InviteDialogTextField from './InviteDialogTextField';

interface IProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles(theme => ({
  inviteLink: {
    marginLeft: theme.spacing(1),
  },
  input: {
    margin: theme.spacing(4, 0, 6),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  cancelButton: {
    marginRight: theme.spacing(2),
  },
}));

const initialValues: CreateClientInvitationInput = {
  email_addresses: [],
};

const InviteDialog: FC<IProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const notify = useNotify();
  const { email_domains } = useProtectedClient();

  const schema = object().shape({
    email_addresses: array()
      .of(
        string()
          .email(t('invites:invalidEmail'))
          .test(
            'correct-domain',
            t('invites:clientDomainError', { clientDomain: email_domains[0] }),
            val => {
              return includes(email_domains, val.split('@')[1]);
            }
          )
          .required(t('admin:emailRequired'))
      )
      .min(1, t('admin:emailRequired'))
      .required(),
  });

  const { mutate, isLoading } = useCreateClientInvitationMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      onClose();
      notify.info({ message: t('invites:inviteSuccess') });
    },
  });

  function handleFormSubmit(values: CreateClientInvitationInput) {
    return mutate({ data: values });
  }

  function renderButtons(isValid: boolean, values: CreateClientInvitationInput) {
    return (
      <Grid container alignItems="center" justify="space-between">
        <Grid item xs={7}></Grid>

        <Grid item xs={5} alignItems="center" container justify="flex-end">
          <Grid item xs={5} onClick={onClose} className={classes.cancelButton}>
            <Button variant="outlined" size="large" fullWidth>
              {t('common:cancel')}
            </Button>
          </Grid>

          <Grid item xs={5}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isEmpty(values.email_addresses) || !isValid || isLoading}
              size="large"
              fullWidth
            >
              {isLoading ? <Loaders.Standard height={26} /> : t('common:send')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Dialog open={open} onBackdropClick={onClose}>
      <Formik onSubmit={handleFormSubmit} initialValues={initialValues} validationSchema={schema}>
        {({ handleSubmit, isValid, values }) => (
          <form
            onKeyDown={event => {
              if (event.key == 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                return false;
              }
              return false;
            }}
            onSubmit={event => {
              event.preventDefault();
              event.stopPropagation();
              handleSubmit();
              return false;
            }}
          >
            <DialogContent>
              <Grid container direction="column" alignItems="center" className={classes.content}>
                <Grid item xs>
                  <Typography variant="h6" align="center">
                    {t('invites:intro')}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography color="textSecondary" align="center">
                    {t('invites:body')}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container className={classes.input}>
                <InviteDialogTextField />
              </Grid>

              {renderButtons(isValid, values)}
            </DialogContent>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default InviteDialog;
